import { useEffect } from 'react';
import { mineAudio } from './mine.js';

/**
 * Wires the mine's sound to the app (§18).
 *
 * Three separate things have to line up, and each is its own effect so that a
 * change in one does not restart the others:
 *
 *   1. **A gesture unlocks it.** Browsers will not start audio without one.
 *      Listening once at the document is what turns the user's first tap —
 *      wherever it lands — into permission, so nobody has to press a "enable
 *      sound" button that exists only to satisfy the platform.
 *   2. **The setting decides whether it is heard**, and the volume with it.
 *   3. **The mining state decides whether it is playing.** This is called on
 *      every render of the mine, including every countdown tick, so the engine
 *      ignores a value it already has.
 *
 * A hidden tab is silent: `visibilitychange` stops it, and coming back starts
 * it again only if a shift is still running.
 */
export function useMineAudio({
  enabled,
  volume,
  working,
}: {
  /** The user's sound setting. */
  enabled: boolean;
  /** 0 to 1. */
  volume: number;
  /** Whether any miner is on shift right now. */
  working: boolean;
}): void {
  // 1. The first gesture, wherever it lands.
  useEffect(() => {
    const unlock = () => mineAudio.unlock();
    // `pointerdown` rather than `click`: it fires earlier, and a drag that
    // never becomes a click still counts as the gesture the platform wants.
    const events: (keyof DocumentEventMap)[] = ['pointerdown', 'keydown', 'touchend'];
    for (const event of events) document.addEventListener(event, unlock, { passive: true });
    return () => {
      for (const event of events) document.removeEventListener(event, unlock);
    };
  }, []);

  // 2. The setting.
  useEffect(() => {
    mineAudio.setVolume(volume);
    mineAudio.setEnabled(enabled);
  }, [enabled, volume]);

  // 3. The work.
  useEffect(() => {
    mineAudio.setWorking(working);
  }, [working]);

  // A mine running in a pocket is a battery complaint.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) mineAudio.setWorking(false);
      else mineAudio.setWorking(working);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [working]);

  // Leaving the app releases the device's audio hardware.
  useEffect(() => {
    return () => {
      mineAudio.setWorking(false);
    };
  }, []);
}
